import type { Intersection, Object3D, Raycaster } from 'three';
import { SceneMeshRaycastPolicy } from './SceneMeshRaycastPolicy';

/**
 * 按完整查询实测成本选择原生或空间粗筛路径，两条路径保留相同的精确求交语义。
 *
 * 每次只执行一次查询；少量交替采样后保持选择，定期复评以适应几何/位姿/视角变化。
 * 不为提速跳过索引边界校验，不把过期命中结果缓存为下一次输入的结果。
 */
export class MeshRaycastQuery {
  /** 查询所有者不由本对象延长生命周期，各选择通道单独校准。 */
  private records = new WeakMap<object, Map<MeshRaycastQueryChannel, MeshRaycastQueryRecord>>();
  /** 释放时解除宿主粗筛端口。 */
  private options: MeshRaycastQueryOptions | null;
  /** 默认时钟只在采样阶段调用，不为稳态原生查询引入计时成本。 */
  private now: (() => number) | null;

  /** @param options 粗筛端口与可选确定性时钟。 */
  public constructor(options: MeshRaycastQueryOptions) {
    this.options = options;
    this.now = options.now ?? (() => performance.now());
  }

  /**
   * 用当前射线完成一次精确求交；不会修改输入候选。
   * @param candidates 当前候选，顺序和重复项保持原样。
   * @param raycaster 既有射线及 near/far、layers、firstHitOnly 配置。
   * @param owner 查询所有者，例如当前 Handler 的服务对象。
   * @param channel 所有者内独立的选择通道。
   * @param target 可选的结果缓冲，沿用 Raycaster 追加与排序语义。
   * @returns 原生 Intersection 结果，继续由调用方做剖切/拓扑仲裁。
   */
  public intersect(candidates: Object3D[], raycaster: Raycaster, owner: object, channel: MeshRaycastQueryChannel, target?: Intersection[]): Intersection[] {
    const options = this.options;
    const now = this.now;
    if (!options || !now) return target ?? [];
    if (candidates.length < SceneMeshRaycastPolicy.minimumCandidates) return raycaster.intersectObjects(candidates, false, target);
    let channels = this.records.get(owner);
    if (!channels) { channels = new Map(); this.records.set(owner, channels); }
    const firstHitOnly = raycaster.firstHitOnly === true;
    let record = channels.get(channel);
    if (!record || record.candidateCount !== candidates.length || record.firstHitOnly !== firstHitOnly) {
      record = { candidateCount: candidates.length, firstHitOnly, preferredPath: 'native', lastPath: 'native',
        sampleStep: 0, warmupSamples: 2, nativeCost: 0, indexedCost: 0, holdRemaining: 0,
        nativeMinCost: Infinity, nativeMaxCost: -Infinity, indexedMinCost: Infinity, indexedMaxCost: -Infinity,
        nativeMeanMs: 0, indexedMeanMs: 0, calibrations: 0, nativeQueries: 0, indexedQueries: 0, active: false, candidates: [] };
      channels.set(channel, record);
    }
    // 自定义 raycast 可能同步发起另一轮查询，嵌套层不得改写外层正在遍历的候选缓冲。
    if (record.active) return raycaster.intersectObjects(candidates, false, target);
    record.active = true;
    const calibrating = record.holdRemaining === 0;
    const path = calibrating ? (record.sampleStep % 2 === 0 ? 'native' : 'indexed') : record.preferredPath;
    try {
      const start = calibrating ? now() : 0;
      if (path === 'indexed') options.filterCandidates(candidates, raycaster.ray, record.candidates);
      const hits = raycaster.intersectObjects(path === 'indexed' ? record.candidates : candidates, false, target);
      record.lastPath = path;
      if (path === 'native') record.nativeQueries++; else record.indexedQueries++;
      if (calibrating) {
        const duration = now() - start;
        // 每个新上下文的首对查询仅预热，避免一次性索引构建决定之后全部请求。
        if (record.sampleStep >= record.warmupSamples) {
          if (path === 'native') {
            record.nativeCost += duration;
            record.nativeMinCost = Math.min(record.nativeMinCost, duration);
            record.nativeMaxCost = Math.max(record.nativeMaxCost, duration);
          } else {
            record.indexedCost += duration;
            record.indexedMinCost = Math.min(record.indexedMinCost, duration);
            record.indexedMaxCost = Math.max(record.indexedMaxCost, duration);
          }
        }
        record.sampleStep++;
        if (record.sampleStep === record.warmupSamples + SceneMeshRaycastPolicy.samplesPerPath * 2) this.finishCalibration(record);
      } else {
        record.holdRemaining--;
      }
      return hits;
    } finally {
      // 失败仍由调用方处理，不让缓冲或失败样本影响下次查询。
      record.candidates.length = 0;
      record.active = false;
    }
  }

  /** @param owner 查询所有者。 @param channel 选择通道。 @returns 显式统计；从未校准时为空。 */
  public getStats(owner: object, channel: MeshRaycastQueryChannel): MeshRaycastQueryStats | null {
    const record = this.records.get(owner)?.get(channel);
    return record ? { preferredPath: record.preferredPath, lastPath: record.lastPath,
      nativeMeanMs: record.nativeMeanMs, indexedMeanMs: record.indexedMeanMs, calibrations: record.calibrations,
      nativeQueries: record.nativeQueries, indexedQueries: record.indexedQueries, holdRemaining: record.holdRemaining } : null;
  }

  /** 文档/查询上下文替换时清除历史成本，不缓存任何业务命中。 */
  public reset(): void { this.records = new WeakMap(); }

  /** 幂等解除宿主端口与成本记录，不销毁非拥有型空间索引。 */
  public dispose(): void { this.options = null; this.now = null; this.reset(); }

  /** @param record 完成一轮采样的通道记录。 */
  private finishCalibration(record: MeshRaycastQueryRecord): void {
    // 保留中间样本的均值，不分配/排序采样数组；周期复评仍能响应持续的真实成本变化。
    const retainedSamples = SceneMeshRaycastPolicy.samplesPerPath - 2;
    record.nativeMeanMs = (record.nativeCost - record.nativeMinCost - record.nativeMaxCost) / retainedSamples;
    record.indexedMeanMs = (record.indexedCost - record.indexedMinCost - record.indexedMaxCost) / retainedSamples;
    const margin = record.preferredPath === 'indexed' ? 1 + SceneMeshRaycastPolicy.hysteresisRatio : 1 - SceneMeshRaycastPolicy.hysteresisRatio;
    record.preferredPath = record.indexedMeanMs < record.nativeMeanMs * margin ? 'indexed' : 'native';
    record.calibrations++;
    record.holdRemaining = SceneMeshRaycastPolicy.reassessInterval;
    record.sampleStep = record.warmupSamples = 0;
    record.nativeCost = record.indexedCost = 0;
    record.nativeMinCost = record.indexedMinCost = Infinity;
    record.nativeMaxCost = record.indexedMaxCost = -Infinity;
  }
}
